import type { ReactNode } from 'react';
import { _js } from '@ifixit/utils/localize';
import { Badge } from '@ifixit/ui/components/badge';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import type { Guide, Supply } from './types';
import { IntroVideoPlayer } from './IntroVideoPlayer';
import { NoImageThumb } from './NoImageThumb';
import { useWikiHtml } from './useWikiHtml';

const DANGER_FLAGS = new Set(['DANGER', 'DANGEROUS']);

/**
 * The introduction screen: title + meta, an optional danger callout and intro
 * video, the rendered introduction prose, then stacked Tools / Parts / Documents
 * sections. Parity with embed.v4.intro.ejs under its isV5 branch + the
 * #standalone-embed.intro layout.
 */
export function IntroScreen({
   guide,
   className,
   footer,
}: {
   guide: Guide;
   className?: string;
   // The paginated app's pager; the full embed scrolls instead and passes none.
   footer?: ReactNode;
}) {
   const dangerFlag = guide.flags.find(f => DANGER_FLAGS.has(f.flagid));
   const stepCount = guide.steps.length;
   // Intro prose is wiki HTML: force link targets + hydrate any video embed.
   const introRef = useWikiHtml<HTMLDivElement>(guide.introduction_rendered);

   // Featured/intro video shows for a video-only guide always, and for step
   // guides only off ifixit (matches the vanilla showIntroVideo scope).
   const showIntroVideo =
      Boolean(guide.introVideo) && (guide.isVideoOnly || App.siteName !== 'ifixit');

   return (
      <section className={cn('mx-auto block max-w-[760px] px-6 pb-6 pt-20', className)}>
         {dangerFlag && (
            <div className="mb-6 flex gap-3 rounded-lg bg-red-100 p-4 text-red-900">
               <FaIcon name="triangle-exclamation" className="mt-0.5 shrink-0" aria-hidden />
               <div>
                  <h3 className="m-0 font-semibold">{dangerFlag.title}</h3>
                  <p className="m-0 mt-1 text-sm">{dangerFlag.text}</p>
               </div>
            </div>
         )}

         <h1 className="m-0 text-2xl font-semibold leading-[1.25] text-gray-950">{guide.title}</h1>

         <div className="mb-5 mt-3 flex flex-wrap gap-2">
            {stepCount > 0 && (
               <Badge className="badge-lg badge-gray">
                  <FaIcon name="list-ol" aria-hidden />
                  {`${stepCount} ${stepCount === 1 ? _js('Step') : _js('Steps')}`}
               </Badge>
            )}
            {guide.time_required && (
               <Badge className="badge-lg badge-gray">
                  <FaIcon name="clock" aria-hidden />
                  {`${_js('Time Required')}: ${guide.time_required}`}
               </Badge>
            )}
            {guide.difficulty && (
               <Badge className="badge-lg badge-gray">
                  <FaIcon name="gauge" aria-hidden />
                  {guide.difficulty}
               </Badge>
            )}
         </div>

         <div>
            {showIntroVideo && guide.introVideo && <IntroVideoPlayer video={guide.introVideo} />}
            <FeaturedDocument guide={guide} />
            {guide.introduction_rendered && (
               <div
                  ref={introRef}
                  className="text-base leading-[1.6] text-gray-950 [&_a:hover]:underline [&_a]:text-brand-500 [&_a]:no-underline [&_b]:font-semibold [&_h2]:mt-6 [&_h2]:text-xl [&_h2]:font-semibold [&_h3]:mt-6 [&_h3]:text-lg [&_h3]:font-semibold [&_img]:h-auto [&_img]:max-w-full [&_li+li]:mt-1.5 [&_ol]:mb-4 [&_ol]:pl-6 [&_p]:mb-4 [&_strong]:font-semibold [&_ul]:mb-4 [&_ul]:pl-6"
                  // Rendered by the guide API's wiki renderer (server-sanitized HTML).
                  dangerouslySetInnerHTML={{ __html: guide.introduction_rendered }}
               />
            )}
         </div>

         <SupplySection title={_js('Tools')} items={guide.tools} />
         <SupplySection title={_js('Parts')} items={guide.parts} />
         <DocumentsSection guide={guide} />

         {footer}
      </section>
   );
}

function FeaturedDocument({ guide }: { guide: Guide }) {
   const thumbnail = guide.featured_document_thumbnail_url;
   const embedUrl = guide.featured_document_embed_url;
   if (!thumbnail || !embedUrl) {
      return null;
   }

   return (
      <div className="relative mb-[18px] h-[300px] overflow-hidden rounded-2xl">
         <img
            src={thumbnail}
            alt=""
            className="size-full scale-105 object-cover blur-[5px] grayscale"
         />
         <div className="absolute inset-0 flex items-center justify-center bg-black/10">
            <a
               href={embedUrl}
               target="_blank"
               rel="noreferrer"
               className="button-primary button-lg no-underline"
            >
               <span>{_js('Launch PDF')}</span>
               <FaIcon name="expand" aria-hidden />
            </a>
         </div>
      </div>
   );
}

/** Section heading with its count, shared by the supply and document grids. */
function SectionHeading({ title, count }: { title: string; count: number }) {
   return (
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold leading-[1.3] text-gray-950">
         {title}
         <Badge className="badge-gray badge-md font-normal">{count}</Badge>
      </h2>
   );
}

const SECTION_CLASS = 'mt-10';
const SUPPLY_GRID_CLASS = 'grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-2';

function SupplySection({ title, items }: { title: string; items: Supply[] }) {
   if (!items.length) {
      return null;
   }
   return (
      <div className={SECTION_CLASS}>
         <SectionHeading title={title} count={items.length} />
         <div className={SUPPLY_GRID_CLASS}>
            {items.map((item, i) => (
               <SupplyRow key={i} item={item} />
            ))}
         </div>
      </div>
   );
}

function SupplyRow({ item }: { item: Supply }) {
   const inner = (
      <>
         {item.thumbnail ? (
            <div className="flex size-12 flex-none items-center justify-center overflow-hidden rounded-lg border border-gray-300 bg-white">
               <img
                  className="size-full object-contain"
                  src={item.thumbnail}
                  alt=""
                  width={48}
                  height={48}
                  loading="lazy"
               />
            </div>
         ) : (
            <NoImageThumb className="size-12 rounded-lg" />
         )}
         <span className="min-w-0 flex-1 break-words text-sm leading-[18px]">{item.text}</span>
         {item.quantity > 1 && (
            <span className="flex-none font-semibold text-gray-500">×{item.quantity}</span>
         )}
      </>
   );

   return item.url ? (
      <a className={SUPPLY_CARD_CLASS} href={item.url} target="_blank" rel="noreferrer">
         {inner}
      </a>
   ) : (
      <div className={SUPPLY_CARD_CLASS}>{inner}</div>
   );
}

const SUPPLY_CARD_CLASS =
   'flex items-center gap-3 rounded-2xl border border-gray-300 bg-white p-2 text-gray-800 no-underline transition-colors hover:border-gray-500';

// The api/2.0 `icon` is DisplayDocument::getIcon()'s FA4 name, which only
// distinguishes these four types and emits `file-o` for the rest. Map them to
// their FA6 equivalents, matching WikiPages' DocumentsSection.
const DOCUMENT_ICONS: Record<string, string> = {
   'file-word-o': 'file-word',
   'file-excel-o': 'file-excel',
   'file-powerpoint-o': 'file-powerpoint',
   'file-pdf-o': 'file-pdf',
};

function documentIcon(icon?: string): string {
   return (icon && DOCUMENT_ICONS[icon]) ?? 'file-lines';
}

function DocumentsSection({ guide }: { guide: Guide }) {
   if (!guide.documents.length) {
      return null;
   }
   return (
      <div className={SECTION_CLASS}>
         <SectionHeading title={_js('Documents')} count={guide.documents.length} />
         <div className={SUPPLY_GRID_CLASS}>
            {guide.documents.map((doc, i) => (
               <a
                  key={i}
                  className={SUPPLY_CARD_CLASS}
                  href={doc.url}
                  target="_blank"
                  rel="noreferrer"
               >
                  <span className="flex size-12 flex-none items-center justify-center rounded-lg border border-gray-300 bg-white text-xl text-gray-500">
                     <FaIcon name={documentIcon(doc.icon)} aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1 break-words text-sm leading-[18px]">
                     {doc.text}
                  </span>
               </a>
            ))}
         </div>
      </div>
   );
}
